import React, {useEffect, useRef, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import { GLASelector, GlaFilterPreset, type Subledger } from "../GLASelector";
import { DEFAULT_GL_ACCOUNT, GlAccount } from "../../../hooks/useGLAccounts.ts";
import { resolveSubledger, useSubledgerContext } from "../../../contexts/SubledgerContext.tsx";


export interface TransactionToBook {
    id: string;
    generalLedgerAccountId?: string;
    relationName?: string;
    relationIBAN?: string;
    subledgerId?: string | null;
}


function getInitialTransactionGLAccount(generalLedgerAccountId: string | undefined, validTransactionGLAccounts: GlAccount[]): GlAccount {
    const currentId =  generalLedgerAccountId ?? DEFAULT_GL_ACCOUNT.id;
    return validTransactionGLAccounts.find((glAccount) => glAccount.id === currentId) ?? DEFAULT_GL_ACCOUNT ;
}

export function BookTransactionModal({
    transactionToBook,
    onClose,
    isOpen,
    onSubmit,
    isLoading,
    glAccounts,
    glAccountsLoading = false,
}: {
    transactionToBook: TransactionToBook | null;
    onClose: () => void;
    isOpen: boolean;
    onSubmit?: (
        transaction: TransactionToBook,
        newGlAccount: GlAccount,
        feedback: string | null,
        subledgerId: string | null
    ) => void;
    isLoading: boolean;
    glAccounts: GlAccount[];
    glAccountsLoading?: boolean;
}) {
    const { subledgers, canUseSubledgers, isLoading: subledgersLoading } = useSubledgerContext();
    const feedbackTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [newGlAccount, setNewGlAccount] = useState<GlAccount>(DEFAULT_GL_ACCOUNT);
    const [selectedSubledger, setSelectedSubledger] = useState<Subledger | null>(null);
    const disabled = glAccountsLoading || glAccounts.length === 0;
    const initializedForRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            initializedForRef.current = null;
            return;
        }

        if (glAccountsLoading || (canUseSubledgers && subledgersLoading)) {
            return;
        }

        const initializationKey = transactionToBook?.id ?? null;
        if (initializedForRef.current === initializationKey) {
            return;
        }
        initializedForRef.current = initializationKey;

        const initialGlAccount = getInitialTransactionGLAccount(transactionToBook?.generalLedgerAccountId, glAccounts);
        setNewGlAccount(initialGlAccount);
        setSelectedSubledger(resolveSubledger(transactionToBook?.subledgerId, subledgers));
        if (feedbackTextareaRef.current) {
            feedbackTextareaRef.current.value = "";
        }
    }, [isOpen, transactionToBook, glAccounts, glAccountsLoading, subledgers, subledgersLoading, canUseSubledgers]);

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-40">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div>
                            <div className="">
                                <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                                    Categorie veranderen
                                </DialogTitle>

                                <div className="mt-4">
                                    <GLASelector
                                        accounts={glAccounts}
                                        value={newGlAccount}
                                        onChange={setNewGlAccount}
                                        filterPreset={GlaFilterPreset.ALL}
                                        placeholder="Categorie zoeken..."
                                        disabled={disabled}
                                        selectedSubledger={canUseSubledgers ? selectedSubledger : undefined}
                                        onSubledgerChange={canUseSubledgers ? setSelectedSubledger : undefined}
                                    />
                                </div>
                                <div className="mt-6">
                                    <p className="text-sm text-gray-500">
                                        Waarom pas je de categorie aan? Laat ons gerust weten, zo
                                        help je ons de app beter voor je te maken. Dit is helemaal vrijblijvend.
                                        Alvast bedankt!
                                    </p>
                                    <div className="mt-4">
                                        <textarea
                                            id="feedback"
                                            name="feedback"
                                            rows={4}
                                            ref={feedbackTextareaRef}
                                            disabled={glAccountsLoading}
                                            className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm/6 disabled:opacity-60 disabled:cursor-not-allowed"
                                            defaultValue={""}
                                            placeholder="Optionele feedback..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => {
                                    if (transactionToBook) {
                                        onSubmit?.(
                                            transactionToBook,
                                            newGlAccount,
                                            feedbackTextareaRef.current?.value ?? "",
                                            selectedSubledger?.id ?? null
                                        );
                                    }
                                }}
                                className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:col-start-2"
                            >
                                {isLoading ? (
                                    <div role="status">
                                        <svg
                                            aria-hidden="true"
                                            className="w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-white"
                                            viewBox="0 0 100 101"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                fill="currentFill"
                                            />
                                        </svg>
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                ) : (
                                    "Klaar"
                                )}
                            </button>
                            <button
                                type="button"
                                data-autofocus="true"
                                onClick={onClose}
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                            >
                                Terug
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
